<script setup lang="ts">
import { computed, ref } from 'vue';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import Textarea from 'primevue/textarea';
import Message from 'primevue/message';
import { GpxDateTimeRangePicker } from '@gpx-spa/ui';
import { focusPrimeVueInput } from '@/utils/focus';

const generalName = defineModel<string>('name', { required: true });
const generalCarrier = defineModel<string>('carrier', { required: true });
const generalNotes = defineModel<string>('notes', { required: true });
const generalAssetValueCents = defineModel<number | null>('assetValueCents', {
  required: true,
});
const generalPlannedRange = defineModel<[Date | null, Date | null] | null>(
  'plannedRange',
  { required: true },
);

const props = withDefaults(
  defineProps<{
    errors?: { name?: string; plannedRange?: string };
    showPlannedRange?: boolean;
    nameReadOnly?: boolean;
    assetValueReadOnly?: boolean;
  }>(),
  {
    errors: () => ({}),
    showPlannedRange: true,
    nameReadOnly: false,
    assetValueReadOnly: false,
  },
);

const generalErrors = computed(() => props.errors ?? {});

const nameInputRef = ref<{ $el?: HTMLElement } | null>(null);

function focusFirstField(): boolean {
  if (props.nameReadOnly) return false;
  return focusPrimeVueInput(nameInputRef.value);
}

defineExpose({
  focusFirstField,
});

const assetValueDollars = computed<number | null>({
  get() {
    const cents = generalAssetValueCents.value;
    if (cents == null) return null;
    return cents / 100;
  },
  set(value: number | null) {
    if (value == null || Number.isNaN(value)) {
      generalAssetValueCents.value = null;
      return;
    }
    if (value < 0) return;
    generalAssetValueCents.value = Math.round(value * 100);
  },
});
</script>

<template>
  <section class="rounded-lg border border-gray-200 bg-white p-4">
    <div class="grid grid-cols-2 gap-x-[24px] gap-y-[20px]">
      <div>
        <label class="mb-1 block text-sm font-medium text-gpx_gray-100"
          >Reference ID *</label
        >
        <InputText
          ref="nameInputRef"
          v-model="generalName"
          placeholder="SHP-2026-00001"
          fluid
          :readonly="nameReadOnly"
          :invalid="Boolean(generalErrors.name)"
        />
        <Message
          v-if="generalErrors.name"
          severity="error"
          size="small"
          variant="simple"
          class="mt-1"
          :closable="false"
        >
          {{ generalErrors.name }}
        </Message>
      </div>
      <div>
        <label class="mb-1 block text-sm font-medium text-gpx_gray-100"
          >Carrier</label
        >
        <InputText v-model="generalCarrier" placeholder="Carrier" fluid />
      </div>

      <div class="col-span-2">
        <label
          for="shipment-value"
          class="mb-1 block text-sm font-medium text-gpx_gray-100"
        >
          Shipment value (optional)
        </label>
        <InputGroup class="w-full">
          <InputNumber
            v-model="assetValueDollars"
            input-id="shipment-value"
            mode="decimal"
            locale="en-US"
            currency="USD"
            :min="0"
            :min-fraction-digits="2"
            :max-fraction-digits="2"
            placeholder="0.00"
            size="small"
            class="min-w-0 flex-1"
            input-class="w-full"
            :readonly="assetValueReadOnly"
          />
          <InputGroupAddon>
            <span class="text-sm font-semibold text-gpx_gray-100">USD</span>
          </InputGroupAddon>
        </InputGroup>
        <p class="mt-1.5 text-xs text-gpx_gray-200">
          Total declared value of the freight on this shipment. Internal only.
        </p>
      </div>

      <div v-if="showPlannedRange" class="col-span-2">
        <label class="mb-1 block text-sm font-medium text-gpx_gray-100"
          >Planned Pickup &amp; Delivery *</label
        >
        <GpxDateTimeRangePicker
          v-model="generalPlannedRange"
          :number-of-months="2"
          hour-format="12"
          size="small"
          fluid
          show-icon
          icon-display="input"
          date-format="yy-mm-dd"
          placeholder="Select pickup and delivery date & time"
          :invalid="Boolean(generalErrors.plannedRange)"
        />
        <Message
          v-if="generalErrors.plannedRange"
          severity="error"
          size="small"
          variant="simple"
          class="mt-1"
          :closable="false"
        >
          {{ generalErrors.plannedRange }}
        </Message>
      </div>
      <div class="col-span-2">
        <label class="mb-1 block text-sm font-medium text-gpx_gray-100"
          >Notes</label
        >
        <Textarea
          v-model="generalNotes"
          placeholder="Optional notes for this shipment"
          fluid
          :rows="4"
          class="min-h-[6.5rem]"
        />
      </div>
    </div>
  </section>
</template>
